import * as amplitude from '@amplitude/analytics-browser';

/**
 * Analytics Event Interface
 */
interface AnalyticsEvent {
  name: string;
  properties?: Record<string, any>;
}

/**
 * User Traits Interface
 */
interface UserTraits {
  user_id: string;
  username?: string;
  display_name?: string;
  email?: string;
  auth_provider?: 'google' | 'apple';
  signup_date?: string;
  [key: string]: any;
}

/**
 * Analytics Service
 * Amplitude-only analytics for Beemi Webplay
 */
class AnalyticsService {
  private initialized = false;
  private amplitudeEnabled = false;

  /**
   * Initialize analytics service
   */
  async initialize(config: {
    amplitudeApiKey?: string;
    userId?: string;
  }) {
    const { amplitudeApiKey, userId } = config;

    console.log('📊 Initializing Webplay Analytics Service...', {
      amplitudeApiKey: amplitudeApiKey ? '***' : 'not provided',
      userId,
      environment: import.meta.env.VITE_ENVIRONMENT || 'production'
    });

    try {
      // Initialize Amplitude
      if (amplitudeApiKey) {
        amplitude.init(amplitudeApiKey, userId, {
          defaultTracking: {
            sessions: true,
            pageViews: false, // We'll track manually for better control
            formInteractions: false,
            fileDownloads: false
          },
          logLevel: import.meta.env.DEV ? amplitude.Types.LogLevel.Debug : amplitude.Types.LogLevel.Warn,
        });
        
        this.amplitudeEnabled = true;
        console.log('✅ Amplitude initialized successfully');
      } else {
        console.log('⚠️ No Amplitude API key provided - running in mock mode');
      }

      this.initialized = true;

    } catch (error) {
      console.error('❌ Analytics initialization failed:', error);
      this.initialized = false;
    }
  }

  /**
   * Track an event
   */
  track(event: AnalyticsEvent) {
    const { name, properties = {} } = event;

    // Add common properties
    const enrichedProperties = {
      ...properties,
      environment: import.meta.env.VITE_ENVIRONMENT || 'production',
      timestamp: new Date().toISOString(),
      platform: 'webplay'
    };

    try {
      // Track in Amplitude if enabled
      if (this.initialized && this.amplitudeEnabled) {
        amplitude.track(name, enrichedProperties);
      }

      // Always log in development mode
      if (import.meta.env.DEV) {
        console.log('📊 Analytics Event:', name, enrichedProperties);
      }
    } catch (error) {
      console.error('❌ Failed to track event:', name, error);
    }
  }

  /**
   * Identify user and set user properties
   */
  identify(userId: string, traits: UserTraits) {
    if (!this.initialized) {
      console.warn('⚠️ Analytics not initialized, cannot identify user');
      return;
    }

    try {
      // Set user in Amplitude
      if (this.amplitudeEnabled) {
        const identifyEvent = new amplitude.Identify();
        
        Object.entries(traits).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            identifyEvent.set(key, value);
          }
        });

        amplitude.identify(identifyEvent);
        amplitude.setUserId(userId);
      }

      console.log('👤 User identified:', userId);
    } catch (error) {
      console.error('❌ Failed to identify user:', error);
    }
  }

  /**
   * Update user properties
   */
  setUserProperties(properties: Record<string, any>) {
    if (!this.initialized) {
      return;
    }

    try {
      // Update in Amplitude
      if (this.amplitudeEnabled) {
        const identifyEvent = new amplitude.Identify();
        
        Object.entries(properties).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            identifyEvent.set(key, value);
          }
        });

        amplitude.identify(identifyEvent);
      }

      if (import.meta.env.DEV) {
        console.log('👤 User properties updated:', properties);
      }
    } catch (error) {
      console.error('❌ Failed to set user properties:', error);
    }
  }

  /**
   * Increment a user property
   */
  incrementUserProperty(property: string, value: number = 1) {
    if (!this.initialized || !this.amplitudeEnabled) {
      return;
    }

    try {
      const identifyEvent = new amplitude.Identify();
      identifyEvent.add(property, value);
      amplitude.identify(identifyEvent);
    } catch (error) {
      console.error('❌ Failed to increment user property:', error);
    }
  }

  /**
   * Reset user data (on logout)
   */
  reset() {
    if (!this.initialized) {
      return;
    }

    try {
      if (this.amplitudeEnabled) {
        amplitude.reset();
      }

      console.log('🧹 Analytics user data reset');
    } catch (error) {
      console.error('❌ Failed to reset analytics:', error);
    }
  }

  /**
   * Check if analytics is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
export default analyticsService;
